import {Component, OnDestroy, OnInit} from '@angular/core';
import {Column, TableComponent} from "../../components/ui/table/table.component";
import {CardComponent} from "../../components/ui/card/card.component";
import {InputComponent} from "../../components/ui/input/input.component";
import {TranslateModule} from "@ngx-translate/core";
import {SelectComponent} from "../../components/ui/select/select.component";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {combineLatest, debounceTime, firstValueFrom, map, shareReplay, Subscription, switchMap} from "rxjs";
import {HttpParams} from "@angular/common/http";
import {Sort} from "../../models/sort";
import {SortHelper} from "../../utils/sort-helper";
import {ActivatedRoute, Router} from "@angular/router";
import {ProjectService} from "../../services/project.service";
import {Project} from "../../models/project";
import {PointCloudService} from "../../services/point-cloud.service";
import {AsyncPipe, NgIf} from "@angular/common";
import {ButtonComponent} from "../../components/ui/button/button.component";
import {LoaderComponent} from "../../components/ui/loader/loader.component";

enum DownloadType {
  DOWNLOAD_SELECTED = 'DOWNLOAD_SELECTED',
  DOWNLOAD_SELECTED_AS_LAS = 'DOWNLOAD_SELECTED_AS_LAS',
  DOWNLOAD_SELECTED_SHAPEFILE_WITH_TAGS = 'DOWNLOAD_SELECTED_SHAPEFILE_WITH_TAGS',
}

@Component({
  selector: 'app-project-detail-point-clouds',
  standalone: true,
  imports: [
    CardComponent,
    InputComponent,
    TableComponent,
    TranslateModule,
    SelectComponent,
    ReactiveFormsModule,
    AsyncPipe,
    ButtonComponent,
    NgIf,
    LoaderComponent
  ],
  templateUrl: './project-detail-point-clouds.component.html',
  styleUrl: './project-detail-point-clouds.component.scss'
})
export class ProjectDetailPointCloudsComponent implements OnInit, OnDestroy {
  public columns: Column[] = [
    { name: 'id', label: 'page.project-detail-point-clouds.list.id' },
    { name: 'start', label: 'page.project-detail-point-clouds.list.start', date: true },
    { name: 'end', label: 'page.project-detail-point-clouds.list.end', date: true },
    { name: 'duration', label: 'page.project-detail-point-clouds.list.duration', suffix: 'page.project-detail-point-clouds.list.duration.suffix' },
    { name: 'uploadedAt', label: 'page.project-detail-point-clouds.list.uploaded-at', date: true },
    { name: 'uploadedBy.email', label: 'page.project-detail-point-clouds.list.uploaded-by' },
  ];

  public project$ = this.route.parent!.data.pipe(map(data => data['project'] as Project));

  public data$ = combineLatest([this.project$, this.route.queryParams]).pipe(
    switchMap(([project, queryParams]) => {
      let requestParams = new HttpParams();

      if (queryParams['search']) {
        requestParams = requestParams.set('search', queryParams['search']);
      }

      let page = 0;
      if (queryParams['page']) {
        page = +queryParams['page'];
      }

      let sort: Sort | undefined = undefined;
      if (queryParams['sort']) {
        sort = SortHelper.convertStringToSort(queryParams['sort']);
      }

      return this.pointCloudService.list(project, page, sort, requestParams);
    }),
    map(response => ({
      ...response,
      content: response.content.map(pointCloud => ({
        ...pointCloud,
        link: `/panel/projects/${pointCloud.project.id}/point-clouds/detail/${pointCloud.id}`,
      })),
    })),
    shareReplay(1),
  );
  sortOptions = this.columns.map(column => ({ value: column.name, labelKey: column.label }));
  sortControl = new FormControl(this.route.snapshot.queryParams['sort'] ?? this.sortOptions[0].value);
  searchControl = new FormControl(this.route.snapshot.queryParams['search'] ?? '');
  downloadControl = new FormControl(null as DownloadType|null);
  subscriptions: Subscription[] = [];
  downloadOptions = [
    { value: null, labelKey: 'page.project-detail-point-clouds.list.selected.placeholder' },
    { value: DownloadType.DOWNLOAD_SELECTED, labelKey: 'page.project-detail-point-clouds.list.selected.download-selected' },
    { value: DownloadType.DOWNLOAD_SELECTED_AS_LAS, labelKey: 'page.project-detail-point-clouds.list.selected.download-as-las' },
    { value: DownloadType.DOWNLOAD_SELECTED_SHAPEFILE_WITH_TAGS, labelKey: 'page.project-detail-point-clouds.list.selected.download-as-shp' },
  ];
  selection: number[] = [];
  downloading = false;

  constructor(private route: ActivatedRoute, private pointCloudService: PointCloudService, private router: Router) {

  }

  changePage(page: number) {
    this.router.navigate([], {
      queryParams: { page },
      queryParamsHandling: 'merge',
    });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.sortControl.valueChanges.subscribe(value => {
        this.router.navigate([], {
          queryParams: { sort: value, page: 0 },
          queryParamsHandling: 'merge',
        });
      }),
      this.searchControl.valueChanges.pipe(debounceTime(400)).subscribe(value => {
        this.router.navigate([], {
          queryParams: { search: value, page: 0 },
          queryParamsHandling: 'merge',
        });
      }),
      this.downloadControl.valueChanges.subscribe(value => {
        if (value) {
          this.performDownload(value);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  updateSelection(value: number[]) {
    this.selection = value;
  }

  async performDownload(type: DownloadType) {
    if (this.downloading) {
      return;
    }

    const project = await firstValueFrom(this.project$);

    try {
      this.downloading = true;
      let blob: Blob | null = null;
      let extension = '';

      if (type === DownloadType.DOWNLOAD_SELECTED) {
        blob = await firstValueFrom(this.pointCloudService.downloadMultiple(project, this.selection));
        extension = 'ply';
      } else if (type === DownloadType.DOWNLOAD_SELECTED_AS_LAS) {
        blob = await firstValueFrom(this.pointCloudService.convertLasMultiple(project, this.selection));
        extension = 'las';
      } else if (type === DownloadType.DOWNLOAD_SELECTED_SHAPEFILE_WITH_TAGS) {
        blob = await firstValueFrom(this.pointCloudService.tagsShapefileMultiple(project, this.selection));
        extension = 'shp';
      }

      if (!blob) {
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project-${project.id}-point-clouds-${extension}.zip`;

      a.click();
      window.URL.revokeObjectURL(url);

      this.downloading = false;

      this.downloadControl.setValue(null);
    } catch (e) {
      console.error(e);
    }
  }
}
